import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useIntersectionObserver } from 'usehooks-ts';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { CoverImageContainer } from '@app/components';
import { useCoverArtFetch, useInstanceDetailsRoutePath } from '@app/hooks';

import './Carousel.scss';

interface CarouselItemProps {
  id: string;
  title: string;
  index: number;
  totalItems: number;
  contributors?: { name: string }[];
  sourceTypes?: string;
  isbns?: string[];
}

const cnBem = createBemBlockBuilder(['carousel']);

export const CarouselItem: FC<CarouselItemProps> = props => {
  const { isIntersecting, ref } = useIntersectionObserver({});

  const { id, title, index, totalItems, contributors, sourceTypes, isbns } =
    props;

  const { $t } = useIntl();
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const { data: coverArtSrc, isInitialLoading: isCoverArtLoading } =
    useCoverArtFetch(isbns, isIntersecting);
  const tabIndex = isIntersecting ? 0 : -1;

  return (
    <Link
      to={getInstanceDetailsRoutePath(id)}
      className={cnBem('__item-link')}
      tabIndex={tabIndex}
    >
      <div
        ref={ref}
        className={cnBem('__item')}
        role="group"
        aria-label={$t(
          {
            id: 'carousel.item',
            defaultMessage: '{title}. {index} slide of {totalItems}',
          },
          { title, index, totalItems }
        )}
        data-auto="carousel-item"
      >
        <div className={cnBem('__imageBox')} data-auto="carousel-item-image">
          <CoverImageContainer
            src={coverArtSrc}
            sourceTypes={sourceTypes}
            isLoading={isCoverArtLoading}
            className={cnBem('__defaultImageWrapper')}
          />
        </div>
        {sourceTypes && (
          <span
            data-auto="carousel-item-source-type"
            className={cnBem('__truncated')}
          >
            {sourceTypes}
          </span>
        )}
        {title && (
          <h3 className={cnBem('__title')} data-auto="carousel-item-title">
            {title}
          </h3>
        )}
        {contributors && !isEmpty(contributors) && (
          <span
            className={cnBem('__truncated')}
            data-auto="carousel-item-contributor"
          >
            {contributors.map(({ name }) => name).join('; ')}
          </span>
        )}
      </div>
    </Link>
  );
};
